import React, { useState } from 'react'
import { Box, Grid, Tooltip } from '@mui/material'
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { type KycArchiveViewProps } from './KycArchiveApplicationPage'
import {
  useGetKycArchiveSecurityTestsQuery,
  useGetKycArchiveTransactionsQuery
} from '../../../api-services/kyc-archive/kycArchiveApi'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { formatDateTimeAsApiFormat } from '../../../utility/formatters/DateFormatter'

const KycArchiveEvaluationsView: React.FC<KycArchiveViewProps> = ({
  applicationId
}: KycArchiveViewProps) => {
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)

  const getTransactionsApiResult = useGetKycArchiveTransactionsQuery(applicationId)

  const skipSecurityTestsQuery = selectedTransactionId === null
  const getSecurityTestsApiResult = useGetKycArchiveSecurityTestsQuery(selectedTransactionId ?? '', { skip: skipSecurityTestsQuery })

  const handleRowClick = (params: GridRowParams): void => {
    setSelectedTransactionId(params.row.id)
  }

  const transactionColumns: GridColDef[] = [
    {
      field: 'created',
      headerName: 'Created',
      flex: 35,
      editable: false,
      valueFormatter: params => (params.value != null ? formatDateTimeAsApiFormat(new Date(params.value as string)) : '-')
    },
    { field: 'merchantDecision', headerName: 'Decision', flex: 25, editable: false },
    { field: 'ednaResult', headerName: 'eDNA Result', flex: 25, editable: false },
    { field: 'state', headerName: 'State', flex: 20, editable: false },
    { field: 'status', headerName: 'Status', flex: 20, editable: false },
    { field: 'riskScore', headerName: 'Risk', flex: 15, editable: false, align: 'right' },
    {
      field: 'graphRiskScore',
      headerName: 'Graph Risk',
      flex: 20,
      editable: false,
      align: 'right',
      valueFormatter: params => (params.value != null ? Number(params.value).toFixed(1) : '-')
    },
    { field: 'deviceTrustScore', headerName: 'Device Trust', flex: 20, editable: false, align: 'right' },
    { field: 'userTrustScore', headerName: 'User Trust', flex: 20, editable: false, align: 'right' },
    { field: 'deviceMatchScore', headerName: 'Device Match', flex: 20, editable: false, align: 'right' },
    { field: 'userMatchScore', headerName: 'User Match', flex: 20, editable: false, align: 'right' },
    {
      field: 'fraudRuleId',
      headerName: 'Fraud Rule',
      flex: 25,
      editable: false,
      renderCell: params => (
        <Tooltip title={params.value ?? '-'}><span>{params.value ?? '-'}</span></Tooltip>
      )
    },
    {
      field: 'resultCodes',
      headerName: 'Result Codes',
      flex: 40,
      editable: false,
      renderCell: params => (
        <Tooltip title={params.value ?? '-'}><span>{params.value ?? '-'}</span></Tooltip>
      )
    },
    { field: 'ipCountry', headerName: 'IP Country', flex: 20, editable: false }
  ]

  const securityTestColumns: GridColDef[] = [
    { field: 'testId', headerName: 'Test', flex: 35, editable: false },
    { field: 'testResult', headerName: 'Result', flex: 30, editable: false },
    { field: 'testOperator', headerName: 'Operator', flex: 20, editable: false },
    { field: 'testRight', headerName: 'Condition', flex: 25, editable: false },
    {
      field: 'testFired',
      headerName: 'Fired',
      flex: 15,
      editable: false,
      renderCell: params => (
        params.value === true ? <CheckCircleOutlineIcon color={'error'}/> : <HighlightOffIcon color={'disabled'}/>
      )
    },
    { field: 'ruleId', headerName: 'Rule Id', flex: 20, editable: false },
    {
      field: 'ruleName',
      headerName: 'Rule Name',
      flex: 40,
      editable: false,
      renderCell: params => (
        <Tooltip title={params.value ?? '-'}><span>{params.value ?? '-'}</span></Tooltip>
      )
    },
    { field: 'ruleResult', headerName: 'Rule Result', flex: 25, editable: false }
  ]

  return (
    <>
      <Box sx={{ pb: 4 }}>
        <h3>Evaluations</h3>
        {getTransactionsApiResult.isLoading && <LoadingComponent/>}
        {(getTransactionsApiResult.error != null) &&
          <ErrorComponent message={RtkQueryHelper.mapResponseError(getTransactionsApiResult.error)}/>}
        {getTransactionsApiResult.isSuccess && (
          <Grid container spacing={4}>
            <Grid item md={12}>
              <DataGrid rows={getTransactionsApiResult.data}
                        columns={transactionColumns}
                        autoHeight={true}
                        onRowClick={handleRowClick}
                        pageSize={10}
                        rowsPerPageOptions={[10]} />
            </Grid>
          </Grid>
        )}
      </Box>

      {selectedTransactionId !== null && (
        <Box sx={{ pb: 4 }}>
          <h3>Security tests for transaction {selectedTransactionId}</h3>
          {getSecurityTestsApiResult.isLoading && <LoadingComponent/>}
          {(getSecurityTestsApiResult.error != null) &&
            <ErrorComponent message={RtkQueryHelper.mapResponseError(getSecurityTestsApiResult.error)}/>}
          {getSecurityTestsApiResult.isSuccess && (
            <Grid container spacing={4}>
              <Grid item md={12}>
                <DataGrid rows={getSecurityTestsApiResult.data}
                          columns={securityTestColumns}
                          autoHeight={true}
                          pageSize={25}
                          rowsPerPageOptions={[25]} />
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* ==================================================================================================== */}
    </>
  )
}

export default KycArchiveEvaluationsView
